/**
 * OrganizationFilterDropdown — popover dropdown for picking an organization
 * on the /strategy entity list screens (Goals, Initiatives, Processes,
 * Services, Risks).
 *
 * Renders a portal-positioned popover anchored to the trigger button so it
 * always stays attached to the field AND escapes the parent stacking context
 * (the previous absolute-positioned version was getting hidden underneath
 * sibling cards). Same web-portal pattern used by RisksScreen for its other
 * filter dropdowns.
 *
 * Single-org users (org_employee / org_executive) only ever see their own
 * org, so the component returns null for them.
 */

import React, { useRef, useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Modal,
  Platform,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from './Icons';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';
import { useAuth } from '../contexts/AuthContext';
import { useAutoTranslate } from '../hooks/useAutoTranslate';
import { useStrategyOrganizationsQuery } from '../hooks/queries';
import { sortOrgsByArabicName } from '../utils/orgSort';
import { FONTS } from '../constants/config';

import { tokens } from '@design';
type ViewAllRole =
  | 'strategy_team'
  | 'executive'
  | 'superuser'
  | 'performance_evaluator'
  | 'evaluation_approver';
const VIEW_ALL_ROLES: readonly ViewAllRole[] = [
  'strategy_team',
  'executive',
  'superuser',
  'performance_evaluator',
  'evaluation_approver',
];

/**
 * True when the current user is allowed to filter across multiple orgs.
 * org_employee / org_executive are auto-scoped to their own org by the
 * backend, so no client-side filter is needed for them.
 */
export function useCanViewAllOrgs(): boolean {
  const { user } = useAuth();
  const userType = user?.profile?.user_type as string | undefined;
  return !!userType && (VIEW_ALL_ROLES as readonly string[]).includes(userType);
}

interface PopoverPos {
  top: number;
  left: number;
  width: number;
}

interface OrganizationFilterDropdownProps {
  selectedOrgId: number | null;
  onChange: (id: number | null) => void;
  style?: StyleProp<ViewStyle>;
  /**
   * When true, renders the dropdown even for single-org users. Default
   * false — the dropdown hides itself for org_employee / org_executive.
   */
  showForSingleOrg?: boolean;
  /**
   * When false, hides the "All Organizations" row — the caller must always
   * keep one concrete org selected (e.g. the evaluator's Human Capital list,
   * where mixing every org's employees together would be meaningless).
   * Default true.
   */
  allowAll?: boolean;
}

export function OrganizationFilterDropdown({
  selectedOrgId,
  onChange,
  style,
  showForSingleOrg = false,
  allowAll = true,
}: OrganizationFilterDropdownProps) {
  const canViewAllOrgs = useCanViewAllOrgs();
  const { colors } = useTheme();
  const { t } = useLanguage();
  const { translateOrg } = useAutoTranslate();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PopoverPos>({ top: 0, left: 0, width: 200 });
  const fieldRef = useRef<View | null>(null);

  const { data: organizations, isLoading } = useStrategyOrganizationsQuery();

  const orgs = useMemo(() => sortOrgsByArabicName(organizations ?? []), [organizations]);

  if (!canViewAllOrgs && !showForSingleOrg) return null;

  const selectedOrg = selectedOrgId == null ? null : orgs.find((o) => o.id === selectedOrgId);
  const displayLabel = selectedOrg
    ? translateOrg(selectedOrg.name) || selectedOrg.name
    : allowAll
      ? t('filters.allOrganizations')
      : t('filters.selectOrganization');

  const handleSelect = (id: number | null) => {
    onChange(id);
    setOpen(false);
  };

  // Measure the trigger and open the popover anchored beneath it.
  const handleOpen = () => {
    if (Platform.OS === 'web' && fieldRef.current) {
      const node = fieldRef.current as unknown as HTMLElement;
      const rect = node.getBoundingClientRect?.();
      if (rect) {
        setPos({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 220),
        });
      }
    } else if (fieldRef.current && (fieldRef.current as any).measureInWindow) {
      (fieldRef.current as any).measureInWindow(
        (x: number, y: number, w: number, h: number) => {
          setPos({ top: y + h + 4, left: x, width: Math.max(w, 220) });
        },
      );
    }
    setOpen(true);
  };

  const renderMenuContent = () => (
    <ScrollView style={{ maxHeight: 320 }} nestedScrollEnabled>
      {allowAll && (
        <TouchableOpacity
          style={[styles.option, { borderBottomColor: colors.border }]}
          onPress={() => handleSelect(null)}
        >
          <Text
            style={[
              styles.optionText,
              { color: colors.textPrimary },
              selectedOrgId == null && { fontFamily: FONTS.medium },
            ]}
          >
            {t('filters.allOrganizations')}
          </Text>
        </TouchableOpacity>
      )}

      {isLoading ? (
        <View style={styles.loading}>
          <ActivityIndicator size="small" color={colors.primary} />
        </View>
      ) : (
        orgs.map((org) => (
          <TouchableOpacity
            key={org.id}
            style={[styles.option, { borderBottomColor: colors.border }]}
            onPress={() => handleSelect(org.id)}
          >
            <Text
              style={[
                styles.optionText,
                { color: colors.textPrimary },
                selectedOrgId === org.id && { fontFamily: FONTS.medium },
              ]}
              numberOfLines={2}
            >
              {translateOrg(org.name)}
            </Text>
          </TouchableOpacity>
        ))
      )}
    </ScrollView>
  );

  return (
    <View style={[styles.container, style]}>
      <View ref={fieldRef as any} collapsable={false}>
        <TouchableOpacity
          style={[
            styles.field,
            { backgroundColor: colors.card, borderColor: colors.cardBorder },
          ]}
          onPress={() => (open ? setOpen(false) : handleOpen())}
          activeOpacity={0.7}
        >
          <ChevronDownIcon size={16} color={colors.textSecondary} />
          <Text
            style={[styles.fieldText, { color: colors.textPrimary }]}
            numberOfLines={1}
          >
            {displayLabel}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Web: portal popover anchored to the button (escapes parent stacking) */}
      {Platform.OS === 'web' && open && createPortal(
        <>
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9998,
            }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: pos.top,
              insetInlineStart: pos.left,
              width: pos.width,
              zIndex: 9999,
              backgroundColor: colors.card,
              borderRadius: tokens.radii.absolute["radius-8"],
              border: `1px solid ${colors.cardBorder}`,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
            }}
          >
            {renderMenuContent()}
          </div>
        </>,
        document.body,
      )}

      {/* Native: Modal fallback (web doesn't take this branch) */}
      {Platform.OS !== 'web' && (
        <Modal
          transparent
          visible={open}
          animationType="fade"
          onRequestClose={() => setOpen(false)}
        >
          <Pressable style={styles.modalBackdrop} onPress={() => setOpen(false)}>
            <Pressable
              style={[
                styles.modalMenu,
                { backgroundColor: colors.card, borderColor: colors.cardBorder },
              ]}
              onPress={(e) => e.stopPropagation?.()}
            >
              {renderMenuContent()}
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </View>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    width: 200,
  },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    minHeight: 40,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
  },
  fieldText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["4"],
  },
  modalMenu: {
    width: '100%',
    maxWidth: 360,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  option: {
    paddingHorizontal: tokens.spacing.absolute["3_5"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
  },
  optionText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  loading: {
    paddingVertical: tokens.spacing.absolute["6"],
    alignItems: 'center',
  },
});

export default OrganizationFilterDropdown;

const styles = getStyles(true);
